import { TIntroducerApplication } from 'lendingApi/applications.types';
import { shouldShowNoAvailableFunds } from 'utils/application/application.helpers';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';

export enum Offer {
  AGREE = 'agree',
  REJECT = 'reject',
  UNDECIDED = 'undecided',
}

export enum ProductType {
  RCF = 'rcf',
  FLEXI = 'flexi',
}

export type TField =
  | 'duration'
  | 'max_amount'
  | 'available_funds'
  | 'product_type'
  | 'offer_id'
  | 'revoked_at'
  | 'loan_agreements'
  | 'timeout'
  | 'total_drawdown_amount'
  | 'interest_rates'
  | 'entity_to_contact'
  | 'withdrawal_fee_percentage'
  | 'monthly_repayments'
  | 'commission'
  | 'initial_commission'
  | 'repeat_commission'
  | 'rcf_withdrawal_fee'
  | 'first_monthly_payment';

export type TColumnData = Partial<
  TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number]
>;

type TApplicationTableData = {
  fields: TField[];
  columns: TColumnData[];
};

function getFields(status: TIntroducerApplication['status']): TField[] {
  let additionalFields: TField[] = [];
  if (
    status === APPLICATION_STATUS.offered ||
    status === APPLICATION_STATUS.talking_to_customer ||
    status === APPLICATION_STATUS.conditional_offer
  ) {
    additionalFields = [
      'available_funds',
      'interest_rates',
      'withdrawal_fee_percentage',
      'initial_commission',
      'repeat_commission',
      'monthly_repayments',
    ];
  } else if (status === APPLICATION_STATUS.offer_rejected) {
    additionalFields = [
      'interest_rates',
      'withdrawal_fee_percentage',
      'initial_commission',
      'repeat_commission',
      'monthly_repayments',
    ];
  } else if (status === APPLICATION_STATUS.expired) {
    additionalFields = [
      'interest_rates',
      'withdrawal_fee_percentage',
      'initial_commission',
      'repeat_commission',
    ];
  } else if (status === APPLICATION_STATUS.funded) {
    additionalFields = [
      'interest_rates',
      'withdrawal_fee_percentage',
      'monthly_repayments',
      'total_drawdown_amount',
    ];
  }
  return ['duration', 'max_amount', ...additionalFields];
}

export const isRequestedAmount = (application: TIntroducerApplication) =>
  [
    APPLICATION_STATUS.open,
    APPLICATION_STATUS.manual_review,
    APPLICATION_STATUS.in_underwriting,
    APPLICATION_STATUS.awaiting_underwriter,
    APPLICATION_STATUS.deferred,
    APPLICATION_STATUS.declined,
    APPLICATION_STATUS.action_required,
  ].includes(application.status) || shouldShowNoAvailableFunds(application);

export function getApplicationTableData(
  application: TIntroducerApplication,
  isRCFView = false,
): TApplicationTableData {
  const {
    status,
    formal_offer_sets,
    requested_products,
    is_partner_channel,
    show_expected_commissions,
  } = application;

  let columns: TColumnData[];
  let fields: TField[] = shouldShowNoAvailableFunds(application)
    ? ['duration', 'max_amount']
    : getFields(status);

  if (!show_expected_commissions) {
    const commissions_fields = ['initial_commission', 'repeat_commission'];
    fields = fields.filter((field) => !commissions_fields.includes(field));
  }

  if (isRCFView) {
    fields = fields
      .map((f) =>
        f === 'withdrawal_fee_percentage' ? 'rcf_withdrawal_fee' : f,
      )
      .map((f) => (f === 'monthly_repayments' ? 'first_monthly_payment' : f));
  }

  const formalOffers = formal_offer_sets?.[0]?.formal_offers;

  const shouldShowFormalOffer =
    formalOffers?.length > 0 && !isRequestedAmount(application);

  if (shouldShowFormalOffer) {
    const selectedOffer = formalOffers?.find((item) => item.is_selected);
    const showAllOfferStatuses: TIntroducerApplication['status'][] = [
      'offered',
      'conditional_offer',
    ];

    const shouldUseSelectedOffer = !showAllOfferStatuses.includes(status);

    if (shouldUseSelectedOffer && selectedOffer && !is_partner_channel) {
      columns = [selectedOffer];
    } else {
      columns = formalOffers;
    }
  } else {
    columns = requested_products.map(({ duration, amount, product_type }) => ({
      duration,
      max_amount: amount,
      product_type,
    }));
  }

  return { columns, fields };
}

export const isRCFOffer = (
  offer: { interest_only_repayments?: number | null },
  isDE: boolean,
) => isDE && offer.interest_only_repayments === 12;

export const isOnlyRCFOffer = (formalOffers: TColumnData[], isDE: boolean) =>
  formalOffers.length === 1 && isDE && isRCFOffer(formalOffers[0], isDE);

export const getProductType = (
  selectedProductType: ProductType | null,
  showProductSelector: boolean,
  hasBothProductTypes: boolean,
  formalOffers: TColumnData[],
  isDE: boolean,
): ProductType | null => {
  if (selectedProductType !== null) return selectedProductType;
  if (showProductSelector || !hasBothProductTypes) return null;
  const accepted = formalOffers.find((o) => o.is_selected);
  if (!accepted) return null;
  return isRCFOffer(accepted, isDE) ? ProductType.RCF : ProductType.FLEXI;
};

export const getVisibleColumns = (
  columns: TColumnData[],
  hasBothProductTypes: boolean,
  selectedProductType: ProductType | null,
  isDE: boolean,
): TColumnData[] => {
  if (!hasBothProductTypes) return columns;
  if (selectedProductType === ProductType.RCF)
    return columns.filter((c) => isRCFOffer(c, isDE));
  if (selectedProductType === ProductType.FLEXI)
    return columns.filter((c) => !isRCFOffer(c, isDE));
  return [];
};
